// eslint-disable max-lines
import { EngineInvariantError, MoveValidationError } from "../errors";
import {
  CAPTURE_FULL,
  INCOME_PER_PROPERTY,
  REPAIR_HP_RATE,
  canRepairOnTile,
  hasActiveCapture,
  isCapturable,
  isIncomeBuilding,
  isProductionBuilding,
  isAntiAirBuildBlocked,
} from "./buildings";
import { canBuildUnitKind } from "./capacity";
import { facingTowardClosestEnemyProduction } from "./facing";
import { getTile, unitAt } from "./grid";
import { normalizedHpDisplay, unitTotalHp } from "./stacks";
import type {
  BuildingTileKind,
  Coord,
  GameEvent,
  GameState,
  PlayerId,
  Unit,
  UnitKind,
} from "./types";
import { isBuildingTile } from "./types";
import { BUILDABLE_BY_BUILDING, MAX_HP, UNIT_DEFS } from "./units";

function applyTilePatch(
  tile: GameState["tiles"][number],
  patch: Partial<GameState["tiles"][number]>,
) {
  const next = { ...tile };
  for (const [key, value] of Object.entries(patch)) {
    if (value === undefined) {
      delete next[key as keyof typeof next];
    } else {
      Object.assign(next, { [key]: value });
    }
  }
  return next;
}

function patchTile(
  state: GameState,
  {
    pos,
    patch,
  }: {
    pos: Coord;
    patch: Partial<GameState["tiles"][number]>;
  },
): GameState {
  const idx = pos.y * state.width + pos.x;
  const tiles = state.tiles.map((t, i) => (i === idx ? applyTilePatch(t, patch) : t));
  return { ...state, tiles };
}

function applyCaptureComplete({
  state,
  unit,
  unitId,
  buildingKind,
}: {
  state: GameState;
  unit: Unit;
  unitId: string;
  buildingKind: BuildingTileKind;
}): { state: GameState; events: GameEvent[] } {
  const afterTile = patchTile(state, {
    pos: unit.pos,
    patch: {
      owner: unit.owner,
      capturePoints: undefined,
    },
  });
  const next = {
    ...afterTile,
    units: afterTile.units.map((u) =>
      u.id === unitId ? { ...u, captures: (u.captures ?? 0) + 1 } : u,
    ),
  };
  return {
    state: next,
    events: [
      {
        playerId: state.currentPlayer,
        type: "captured",
        unitId,
        pos: unit.pos,
        newOwner: unit.owner,
        building: buildingKind,
      },
    ],
  };
}

export function applyCapture(
  state: GameState,
  unitId: string,
): { state: GameState; events: GameEvent[] } {
  const unit = state.units.find((u) => u.id === unitId);
  if (!unit) throw new EngineInvariantError(`Unknown unit ${unitId}`);
  if (!UNIT_DEFS[unit.kind].canCapture)
    throw new MoveValidationError("cannot_capture", `Unit ${unitId} cannot capture`);
  const tile = getTile(state, unit.pos);
  if (!isBuildingTile(tile.kind))
    throw new MoveValidationError("no_building", `No building at ${unit.pos.x},${unit.pos.y}`);
  if (!isCapturable(tile, unit.owner))
    throw new MoveValidationError(
      "already_owned",
      `Building at ${unit.pos.x},${unit.pos.y} already owned by ${unit.owner}`,
    );
  const events: GameEvent[] = [];
  const currentPoints = tile.capturePoints ?? CAPTURE_FULL;
  const reduction = normalizedHpDisplay(unit.hp);
  const remaining = Math.max(0, currentPoints - reduction);
  let next = patchTile(state, {
    pos: unit.pos,
    patch: {
      capturePoints: remaining,
    },
  });
  next = {
    ...next,
    units: next.units.map((u) =>
      u.id === unitId ? { ...u, hasMoved: true, hasAttacked: true } : u,
    ),
  };
  if (remaining <= 0) {
    const done = applyCaptureComplete({
      state: next,
      unit,
      unitId,
      buildingKind: tile.kind,
    });
    return { state: done.state, events: done.events };
  }
  events.push({ type: "captureProgressed", unitId, pos: unit.pos, remaining });
  return { state: next, events };
}

export function resetCaptureAt({
  state,
  pos,
  reason,
}: {
  state: GameState;
  pos: Coord;
  reason: "moved" | "killed";
}): { state: GameState; events: GameEvent[] } {
  const tile = getTile(state, pos);
  if (!hasActiveCapture(tile)) return { state, events: [] };

  const next = patchTile(state, {
    pos,
    patch: { capturePoints: undefined },
  });
  return { state: next, events: [{ type: "captureReset", pos, reason }] };
}

export function assertAntiAirBuildAllowed({
  state,
  unitKind,
}: {
  state: GameState;
  unitKind: UnitKind;
}): void {
  if (
    isAntiAirBuildBlocked({
      state,
      kind: unitKind,
      player: state.currentPlayer,
    })
  ) {
    throw new MoveValidationError("no_airports", "No airports on today's map");
  }
}

function assertCanBuildAt({
  state,
  pos,
  unitKindReq,
}: {
  state: GameState;
  pos: Coord;
  unitKindReq: string;
}): {
  unitKind: keyof typeof UNIT_DEFS;
  def: (typeof UNIT_DEFS)[keyof typeof UNIT_DEFS];
} {
  const tile = getTile(state, pos);
  if (!isBuildingTile(tile.kind))
    throw new MoveValidationError("no_building", `No building at ${pos.x},${pos.y}`);
  if (!isProductionBuilding(tile.kind))
    throw new MoveValidationError("not_buildable_here", `Can only build at a production building`);
  if (tile.owner !== state.currentPlayer)
    throw new MoveValidationError("wrong_owner", `Building not owned by ${state.currentPlayer}`);
  if (unitAt(state, pos))
    throw new MoveValidationError("building_occupied", `Building tile is occupied`);
  if (!UNIT_DEFS[unitKindReq as keyof typeof UNIT_DEFS])
    throw new MoveValidationError("unknown_unit_kind", `Unknown unit kind ${unitKindReq}`);
  const unitKind = unitKindReq as keyof typeof UNIT_DEFS;
  const allowedKinds = BUILDABLE_BY_BUILDING[tile.kind] as string[];
  if (!allowedKinds.includes(unitKindReq))
    throw new MoveValidationError(
      "not_buildable_here",
      `Cannot build ${unitKindReq} at ${tile.kind}`,
    );
  if (
    !canBuildUnitKind({
      state,
      player: state.currentPlayer,
      unitKind,
    })
  ) {
    throw new MoveValidationError(
      "capacity_reached",
      `Capacity reached for ${UNIT_DEFS[unitKind].movementType} units`,
    );
  }
  assertAntiAirBuildAllowed({ state, unitKind });
  return { unitKind, def: UNIT_DEFS[unitKind] };
}

export function applyBuild({
  state,
  pos,
  unitKindReq,
}: {
  state: GameState;
  pos: Coord;
  unitKindReq: string;
}): { state: GameState; events: GameEvent[] } {
  const { unitKind, def } = assertCanBuildAt({ state, pos, unitKindReq });
  const funds = state.funds[state.currentPlayer];
  if (funds < def.cost) throw new MoveValidationError("insufficient_funds", `Insufficient funds`);
  const unitId = `u${state.nextUnitId}`;
  const facing = facingTowardClosestEnemyProduction({
    state,
    owner: state.currentPlayer,
    pos,
    unitKind,
  });
  const newUnit: Unit = {
    id: unitId,
    owner: state.currentPlayer,
    kind: unitKind,
    pos,
    hp: MAX_HP,
    hasMoved: true,
    hasAttacked: true,
    facing,
  };
  const next: GameState = {
    ...state,
    funds: { ...state.funds, [state.currentPlayer]: funds - def.cost },
    units: [...state.units, newUnit],
    nextUnitId: state.nextUnitId + 1,
  };
  return {
    state: next,
    events: [
      {
        playerId: state.currentPlayer,
        type: "unitBuilt",
        unitId,
        owner: state.currentPlayer,
        kind: unitKind,
        pos,
        cost: def.cost,
      },
    ],
  };
}

export function applyStartOfTurn(state: GameState): { state: GameState; events: GameEvent[] } {
  const player = state.currentPlayer;
  const events: GameEvent[] = [];

  let next = state;
  const properties = state.tiles.filter(
    (t) => isIncomeBuilding(t.kind) && t.owner === player,
  ).length;
  if (properties > 0) {
    const amount = properties * INCOME_PER_PROPERTY;
    next = { ...next, funds: { ...next.funds, [player]: next.funds[player] + amount } };
    events.push({ type: "incomeCollected", playerId: player, amount, properties });
  }

  const repaired = applyStartOfTurnRepairs(next, player);
  next = repaired.state;
  events.push(...repaired.events);

  return { state: next, events };
}

function applyStartOfTurnRepairs(
  state: GameState,
  player: PlayerId,
): { state: GameState; events: GameEvent[] } {
  const events: GameEvent[] = [];
  let next = state;

  for (const unit of state.units) {
    if (unit.owner !== player) continue;
    const def = UNIT_DEFS[unit.kind];
    const currentHp = unitTotalHp(unit);
    if (currentHp <= 0 || currentHp >= MAX_HP) continue;

    const tile = state.tiles[unit.pos.y * state.width + unit.pos.x];
    if (!tile || !isBuildingTile(tile.kind) || tile.owner !== player) continue;
    if (!canRepairOnTile(unit.kind, tile.kind)) continue;

    const maxHpStep = Math.round(MAX_HP * REPAIR_HP_RATE);
    const maxCostStep = Math.round(def.cost * REPAIR_HP_RATE);
    if (maxHpStep <= 0) continue;

    const hpRestored = Math.min(maxHpStep, MAX_HP - currentHp);
    const repairCost =
      hpRestored >= maxHpStep ? maxCostStep : Math.ceil((hpRestored / maxHpStep) * maxCostStep);

    if (next.funds[player] < repairCost) continue;

    next = {
      ...next,
      funds: { ...next.funds, [player]: next.funds[player] - repairCost },
      units: next.units.map((u) => (u.id === unit.id ? { ...u, hp: currentHp + hpRestored } : u)),
    };
    events.push({
      playerId: player,
      type: "unitRepaired",
      unitId: unit.id,
      pos: unit.pos,
      hpRestored,
      cost: repairCost,
    });
  }

  return { state: next, events };
}
